import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { formatEnumValue } from '@/lib/formatters'
import type { SyncRunDetail } from '@/features/chronograph-dashboard/types'
import { useSyncRunDetail } from '@/hooks/use-chronograph'
import {
  countSkipIssues,
  formatDateTime,
  formatFieldLabel,
  formatParameterValue,
  formatSyncTypeLabel,
  getSkipSeverity,
  isPrimitiveValue,
  sortSkipsBySeverity,
  StatusBadge,
  toShortId,
} from '@/components/custom/chronograph/shared'

interface RunDetailSheetProps {
  selectedRunId: string | null
  onClose: () => void
}

export function RunDetailSheet({
  selectedRunId,
  onClose,
}: RunDetailSheetProps) {
  const runDetail = useSyncRunDetail(selectedRunId)

  return (
    <Sheet
      open={Boolean(selectedRunId)}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        {runDetail.isError ? (
          <div className="flex flex-col items-center gap-3 p-6">
            <Typography
              variant="small"
              className="font-medium text-destructive"
            >
              Failed to load run details.
            </Typography>
            <Button
              variant="outline"
              size="sm"
              onClick={() => runDetail.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : runDetail.data ? (
          <RunDetailsPanel run={runDetail.data} />
        ) : runDetail.isLoading ? (
          <div className="space-y-4 p-6">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

function RunDetailsPanel({ run }: { run: SyncRunDetail }) {
  const parameterEntries: Array<[string, string | number | boolean | null]> =
    run.parameters
      ? Object.entries(run.parameters).flatMap(([key, value]) =>
          isPrimitiveValue(value) ? [[key, value]] : []
        )
      : []
  const canRenderParameterGrid =
    Boolean(run.parameters) &&
    parameterEntries.length > 0 &&
    parameterEntries.length === Object.keys(run.parameters ?? {}).length

  const totalChanged = run.created + run.updated

  return (
    <div className="space-y-6">
      {/* Header */}
      <SheetHeader className="border-b border-primary/12 pb-5">
        <div className="flex items-start justify-between gap-3 pr-8">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2.5">
              <SheetTitle className="font-mono text-xl font-semibold leading-tight text-primary">
                {toShortId(run.id)}
              </SheetTitle>
              <StatusBadge status={run.status} />
            </div>
            <Typography
              variant="body-sm"
              className="select-all font-mono text-primary/70"
            >
              {run.id}
            </Typography>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Badge variant="outline">{formatSyncTypeLabel(run.type)}</Badge>
          <Badge variant="outline">{formatEnumValue(run.trigger)}</Badge>
          {run.user ? (
            <Typography variant="body-sm" className="text-primary/70">
              by {run.user.name}
            </Typography>
          ) : null}
        </div>
      </SheetHeader>

      {/* Key stats — prominent blocks */}
      <div className="grid grid-cols-2 gap-3">
        <StatBlock label="Duration" value={run.duration ?? '\u2014'} />
        <StatBlock
          label="Impact"
          value={
            totalChanged > 0
              ? `${totalChanged.toLocaleString()} changed`
              : 'No changes'
          }
          tone={totalChanged > 0 ? 'positive' : 'neutral'}
        />
        <StatBlock
          label="Created"
          value={run.created.toLocaleString()}
          tone={run.created > 0 ? 'positive' : 'neutral'}
        />
        <StatBlock
          label="Updated"
          value={run.updated.toLocaleString()}
          tone={run.updated > 0 ? 'info' : 'neutral'}
        />
        <StatBlock label="Skipped" value={run.skipped.toLocaleString()} />
        <StatBlock
          label="Errors"
          value={run.errored.toLocaleString()}
          tone={run.errored > 0 ? 'danger' : 'neutral'}
        />
      </div>

      {/* Secondary info */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-2.5 rounded-lg bg-primary/[0.02] px-4 py-3">
        <KVRow label="Started" value={formatDateTime(run.startedAt)} />
        <KVRow label="Completed" value={formatDateTime(run.completedAt)} />
        <KVRow label="Batch" value={toShortId(run.batchId)} />
        {run.user ? <KVRow label="Triggered by" value={run.user.name} /> : null}
      </div>

      {/* Parameters */}
      {run.parameters ? (
        <div className="space-y-2.5">
          <Typography variant="small" className="font-semibold text-primary/80">
            Parameters
          </Typography>
          {canRenderParameterGrid ? (
            <div className="grid grid-cols-2 gap-x-8 gap-y-2.5 rounded-lg border border-primary/8 bg-primary/[0.02] px-4 py-3.5">
              {parameterEntries.map(([key, value]) => (
                <KVRow
                  key={key}
                  label={formatFieldLabel(key)}
                  value={formatParameterValue(value)}
                />
              ))}
            </div>
          ) : (
            <pre className="overflow-x-auto rounded-md bg-primary/[0.03] p-3 text-xs text-primary/70">
              {JSON.stringify(run.parameters, null, 2)}
            </pre>
          )}
        </div>
      ) : null}

      {/* Error Samples */}
      {run.errorSamples && run.errorSamples.length > 0 ? (
        <div className="rounded-lg border border-destructive/15 bg-destructive/[0.03] px-4 py-3.5">
          <Typography
            variant="body-sm"
            className="font-semibold text-destructive/80"
          >
            Errors ({run.errorSamples.length})
          </Typography>
          <div className="mt-2 space-y-1.5">
            {run.errorSamples.map((error, index) => (
              <Typography
                key={`${index}-${error}`}
                variant="body-sm"
                className="text-primary/70"
              >
                &bull; {error}
              </Typography>
            ))}
          </div>
        </div>
      ) : null}

      {/* Skip Breakdown */}
      {run.skipBreakdown && run.skipBreakdown.length > 0 ? (
        <SkipBreakdownPanel breakdown={run.skipBreakdown} />
      ) : null}
    </div>
  )
}

function StatBlock({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: string
  tone?: 'neutral' | 'positive' | 'info' | 'danger'
}) {
  const borderColor =
    tone === 'positive'
      ? 'border-emerald-500/20'
      : tone === 'info'
        ? 'border-blue-500/20'
        : tone === 'danger'
          ? 'border-destructive/20'
          : 'border-primary/10'

  const valueColor =
    tone === 'positive'
      ? 'text-emerald-600'
      : tone === 'info'
        ? 'text-blue-600'
        : tone === 'danger'
          ? 'text-destructive'
          : 'text-primary'

  return (
    <div
      className={cn(
        'rounded-lg border bg-background px-3.5 py-2.5',
        borderColor
      )}
    >
      <Typography
        variant="body-sm"
        className="text-[11px] uppercase tracking-wide text-primary/70"
      >
        {label}
      </Typography>
      <Typography
        variant="body-sm"
        className={cn(
          'mt-0.5 font-inter text-lg font-semibold tabular-nums',
          valueColor
        )}
      >
        {value}
      </Typography>
    </div>
  )
}

function KVRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <Typography variant="body-sm" className="text-primary/70">
        {label}
      </Typography>
      <Typography
        variant="body-sm"
        className="text-right font-inter font-medium tabular-nums"
      >
        {value}
      </Typography>
    </div>
  )
}

function SkipBreakdownPanel({
  breakdown,
}: {
  breakdown: NonNullable<
    import('@/features/chronograph-dashboard/types').SyncRunDetail['skipBreakdown']
  >
}) {
  const totalCount = breakdown.reduce((sum, item) => sum + item.count, 0)
  const issueCount = countSkipIssues(breakdown)
  const sorted = sortSkipsBySeverity(breakdown)

  const severityStyles = {
    error: {
      bg: 'bg-destructive/[0.04]',
      border: 'border-destructive/15',
      dot: 'bg-destructive',
      text: 'text-destructive/80',
      count: 'text-destructive/70',
    },
    warning: {
      bg: 'bg-amber-500/[0.04]',
      border: 'border-amber-500/15',
      dot: 'bg-amber-500',
      text: 'text-amber-700/80',
      count: 'text-amber-700/70',
    },
    healthy: {
      bg: 'bg-primary/[0.02]',
      border: 'border-primary/8',
      dot: 'bg-primary/20',
      text: 'text-primary/70',
      count: 'text-primary/70',
    },
  } as const

  return (
    <div className="rounded-lg border border-primary/12 bg-primary/[0.02] px-4 py-3.5">
      <div className="mb-3 flex items-center justify-between">
        <Typography variant="small" className="font-semibold text-primary">
          Skip Breakdown
        </Typography>
        <div className="flex items-center gap-1.5">
          {issueCount > 0 ? (
            <Badge
              variant="outline"
              className="border-amber-500/25 bg-amber-500/[0.06] font-inter tabular-nums text-amber-700"
            >
              {issueCount.toLocaleString()}{' '}
              {issueCount === 1 ? 'issue' : 'issues'}
            </Badge>
          ) : null}
          <Badge
            variant="outline"
            className="border-primary/15 font-inter tabular-nums"
          >
            {totalCount.toLocaleString()} total
          </Badge>
        </div>
      </div>
      <div className="space-y-2">
        {sorted.map((item) => {
          const severity = getSkipSeverity(item.reason)
          const styles = severityStyles[severity]

          return (
            <div
              key={item.reason}
              className={cn(
                'rounded-md px-3 py-2',
                styles.bg,
                severity !== 'healthy' && `border ${styles.border}`
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  <div
                    className={cn('size-1.5 shrink-0 rounded-full', styles.dot)}
                  />
                  <Typography
                    variant="body-sm"
                    className={cn('text-xs font-medium', styles.text)}
                  >
                    {formatEnumValue(item.reason)}
                  </Typography>
                </div>
                <Typography
                  variant="body-sm"
                  className={cn(
                    'font-inter text-sm font-semibold tabular-nums',
                    styles.count
                  )}
                >
                  {item.count.toLocaleString()}
                </Typography>
              </div>
              {item.description ? (
                <Typography
                  variant="body-sm"
                  className={cn(
                    'mt-1 text-[11px] leading-relaxed',
                    severity === 'healthy'
                      ? 'text-primary/70'
                      : 'text-primary/70'
                  )}
                >
                  {item.description}
                </Typography>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}
